import { Link } from "@inertiajs/react"
import { Eye, MoreHorizontal, Pencil, UserPlus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { assignMissionPath, editMissionPath, missionPath } from "@/routes"
import type { MissionRow } from "@/types/missions"

/**
 * OKS-31 — Per-row quick-action kebab for the Brand Mission list.
 *
 * Items (UX Spec §2.1):
 *   - View          (always visible)
 *   - Edit          (visible iff `row.can_edit` — drafts only)
 *   - Assign creators (visible iff `row.can_assign` — actives only)
 *
 * Edit + Assign visibility is server-driven — the controller computes
 * `can_edit` (`MissionPolicy#edit?` + `status=draft`) and `can_assign`
 * (`status=active` + `MissionAssignmentPolicy#show?`) per row, so the
 * FE never re-derives role × state. Cross-brand rows can never reach
 * this component (the brand floor 404s the row out of `missions[]`).
 *
 * Touch target: shadcn `Button` ghost icon variant is 36px square at
 * `size="icon"`; we bump to `size="default"` on the kebab to stay ≥
 * 44px on mobile (UX Spec §6).
 */
interface MissionListRowActionsProps {
  row: MissionRow
}

export function MissionListRowActions({ row }: MissionListRowActionsProps) {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label={t("missions.index.actions.menu_aria", {
              title: row.title,
            })}
            data-testid="mission-list-row-actions-trigger"
            data-mission-id={row.id}
          >
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        }
      />
      <DropdownMenuContent
        align="end"
        data-testid="mission-list-row-actions-menu"
      >
        <DropdownMenuItem
          render={
            <Link
              href={missionPath(row.id)}
              className="flex items-center gap-2"
              data-testid="mission-list-row-action-view"
            >
              <Eye className="size-4" aria-hidden="true" />
              {t("missions.index.actions.view")}
            </Link>
          }
        />
        {row.can_edit && (
          <DropdownMenuItem
            render={
              <Link
                href={editMissionPath(row.id)}
                className="flex items-center gap-2"
                data-testid="mission-list-row-action-edit"
              >
                <Pencil className="size-4" aria-hidden="true" />
                {t("missions.index.actions.edit")}
              </Link>
            }
          />
        )}
        {row.can_assign && (
          <DropdownMenuItem
            render={
              <Link
                href={assignMissionPath(row.id)}
                className="flex items-center gap-2"
                data-testid="mission-list-row-action-assign"
              >
                <UserPlus className="size-4" aria-hidden="true" />
                {t("missions.index.actions.assign")}
              </Link>
            }
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
